import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { saveResumeLocation } from '../utils/sessionResume';

const INACTIVITY_TIMEOUT_MS = 6 * 60 * 60 * 1000; // 6 hours
const WARNING_DURATION_S = 60; // 60 second countdown
const CHECK_INTERVAL_MS = 10 * 1000; // check every 10 seconds
const THROTTLE_MS = 1000; // throttle activity updates to once per second

const InactivityTimeout: React.FC = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const lastActivityRef = useRef<number>(Date.now());
  const lastThrottleRef = useRef<number>(0);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_DURATION_S);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showWarning) {
      setShowWarning(false);
      setCountdown(WARNING_DURATION_S);
    }
  }, [showWarning]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottleRef.current < THROTTLE_MS) return;
    lastThrottleRef.current = now;
    resetActivity();
  }, [resetActivity]);

  // Reset warning state when user logs out (prevents stale state from triggering
  // an immediate logout when the user logs back in)
  useEffect(() => {
    if (!isAuthenticated) {
      setShowWarning(false);
      setCountdown(WARNING_DURATION_S);
      lastActivityRef.current = Date.now();
    }
  }, [isAuthenticated]);

  // Listen for user activity events (pause while warning is visible)
  useEffect(() => {
    if (!isAuthenticated || showWarning) return;

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, handleActivity));

    return () => {
      events.forEach(e => document.removeEventListener(e, handleActivity));
    };
  }, [isAuthenticated, handleActivity, showWarning]);

  // Check inactivity every 5 seconds (testing) / 10 seconds (production)
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= INACTIVITY_TIMEOUT_MS && !showWarning) {
        setShowWarning(true);
        setCountdown(WARNING_DURATION_S);
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAuthenticated, showWarning]);

  // Countdown timer when warning is visible
  useEffect(() => {
    if (!showWarning) return;

    const interval = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning]);

  // Logout when countdown reaches 0. This is the automatic path (the user did
  // not act), so stash their current location to offer a resume on next login.
  useEffect(() => {
    if (showWarning && countdown === 0) {
      saveResumeLocation(user?.id);
      logout();
    }
  }, [showWarning, countdown, logout, user?.id]);

  if (!isAuthenticated || !showWarning) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        {/* Warning icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
          Session Expiring
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-center mb-4">
          You've been inactive for 6 hours. Your session will expire for security reasons.
        </p>

        {/* Countdown */}
        <div className="text-center mb-6">
          <span className="text-3xl font-mono font-bold text-red-600">
            0:{countdown.toString().padStart(2, '0')}
          </span>
          <p className="text-sm text-gray-500 mt-1">seconds remaining</p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={resetActivity}
            className="w-full px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all"
          >
            Stay Logged In
          </button>
          <button
            onClick={logout}
            className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Log Out Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default InactivityTimeout;
